import { theme, spacing, typography, buttonStyles, mergeStyles, isMobile, mobileSpacing } from '../styles/theme';
import SimpleHeader from '../components/SimpleHeader';

export default function About() {
  return (
    <>
      <SimpleHeader />  
      <div style={{
        backgroundColor: theme.bg.primary,
        position: 'fixed',
        top: '72px',
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        padding: spacing['3xl'],
        ...(isMobile() && {
            top: '70px',
            padding: spacing.lg,
        })
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          backgroundColor: 'white',
          padding: spacing['3xl'],
          borderRadius: '12px',
          boxShadow: theme.shadow.medium,
          ...(isMobile() && {
            padding: spacing.lg,
          })
        }}>
          {/* Back link */}
          <div style={{ marginBottom: spacing.lg }}>
            <a href="/" style={{
              color: theme.brand.primary,
              textDecoration: 'none',
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.medium,
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}>
              ← Back to Judgy
            </a>
          </div>
          <p style={{
            fontSize: typography.fontSize.lg,
            color: theme.text.primary,
            marginBottom: spacing.xl,
            lineHeight: typography.lineHeight.relaxed,
            fontWeight: typography.fontWeight.medium,
            ...(isMobile() && {
              fontSize: typography.mobileFontSize.base,
            })
          }}>
            Judgy is a free AI-powered rules assistant for Magic: The Gathering. Get instant answers to MTG rules questions, card interactions, and game mechanics. Perfect for casual players, Commander enthusiasts, and anyone learning the game.
          </p>
          {/* Header */}
          <h1 style={{
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            marginBottom: spacing.xl,
            color: theme.text.primary,
            fontFamily: typography.fontFamily.primary,
          }}>
            About Judgy
          </h1>

          {/* Tagline */}
          <p style={{
            fontSize: typography.fontSize.xl,
            color: theme.text.secondary,
            marginBottom: spacing['2xl'],
            fontStyle: 'italic',
            ...(isMobile() && {
              fontSize: typography.mobileFontSize.lg,
            })
          }}>
            If you can't ask a judge, ask Judgy.
          </p>

          {/* What is Judgy */}
          <section style={{ marginBottom: spacing['2xl'] }}>
            <h2 style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
              })
            }}>
              What is Judgy?
            </h2>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Judgy is an AI-powered Magic: The Gathering rules assistant designed to help players 
              understand complex card interactions, resolve rules questions, and learn the game's 
              intricate mechanics. Whether you're a casual kitchen-table player puzzled by the stack, 
              or a competitive grinder preparing for tournament play, Judgy provides instant, 
              accurate answers based on the official Comprehensive Rules, additional card rulings and real-world examples.
            </p>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Built with OpenAI's advanced language models and trained on thousands of real MTG 
              rulings and examples, Judgy understands the nuances of Magic's rules in a way that 
              simple rule lookups can't match. It can explain not just what the rules say, but 
              how they interact in practice.
            </p>
          </section>

          {/* Why Judgy Exists */}
          <section style={{ marginBottom: spacing['2xl'] }}>
            <h2 style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
              })
            }}>
              Why Judgy Exists
            </h2>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Magic: The Gathering is one of the most complex games ever created. With over 30,000 
              unique cards and a Comprehensive Rules document exceeding 300 pages, even experienced 
              players encounter situations where the correct ruling isn't immediately clear. While 
              certified judges are invaluable at tournaments, they're not always available for 
              casual play, online matches, or quick rules checks. <strong>But always consult with a
              judge if you have the chance.</strong>
            </p>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Judgy was created to fill that gap: to be your always-available rules expert, helping 
              you understand not just what happens, but why. Whether you're building a deck, 
              resolving a dispute with friends, or learning a new mechanic, Judgy is here to help.
            </p>
          </section>

          {/* What Judgy Can (and Can't) Do */}
          <section style={{ marginBottom: spacing['2xl'] }}>
            <h2 style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
              })
            }}>
              What Judgy Can (and Can't) Do
            </h2>
            
            <div style={{
              backgroundColor: theme.bg.secondary,
              padding: spacing.xl,
              borderRadius: '8px',
              marginBottom: spacing.lg,
              ...(isMobile() && {
                padding: mobileSpacing.lg,
              })
            }}>
              <h3 style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: theme.status.success,
                marginBottom: spacing.md,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.base,
                })
              }}>
                Judgy CAN:
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: spacing.xl,
                color: theme.text.secondary,
                fontSize: typography.fontSize.base,
                lineHeight: typography.lineHeight.relaxed,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.sm,
                  paddingLeft: mobileSpacing.lg,
                })
              }}>
                <li style={{ marginBottom: spacing.sm }}>Answer rules questions with official citations</li>
                <li style={{ marginBottom: spacing.sm }}>Explain complex card interactions step-by-step</li>
                <li style={{ marginBottom: spacing.sm }}>Help you understand game mechanics and keywords</li>
                <li style={{ marginBottom: spacing.sm }}>Provide context on when and why rules work a certain way</li>
                <li style={{ marginBottom: spacing.sm }}>Look up card information instantly using [[double brackets]]</li>
              </ul>
            </div>

            <div style={{
              backgroundColor: theme.bg.secondary,
              padding: spacing.xl,
              borderRadius: '8px',
              ...(isMobile() && {
                padding: mobileSpacing.lg,
              })
            }}>
              <h3 style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: theme.status.error,
                marginBottom: spacing.md,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.base,
                })
              }}>
                Judgy CANNOT:
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: spacing.xl,
                color: theme.text.secondary,
                fontSize: typography.fontSize.base,
                lineHeight: typography.lineHeight.relaxed,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.sm,
                  paddingLeft: mobileSpacing.lg,
                })
              }}>
                <li style={{ marginBottom: spacing.sm }}>Replace certified judges at sanctioned tournaments</li>
                <li style={{ marginBottom: spacing.sm }}>Make official rulings for competitive play</li>
                <li style={{ marginBottom: spacing.sm }}>Know about unreleased or spoiled cards</li>
                <li style={{ marginBottom: spacing.sm }}>Guarantee 100% accuracy in all edge cases</li>
              </ul>
            </div>

            <p style={{
              fontSize: typography.fontSize.sm,
              fontStyle: 'italic',
              color: theme.text.muted,
              marginTop: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xs,
              })
            }}>
              <strong>Important:</strong> Always consult a certified judge for tournament play. 
              Judgy is designed for casual reference and learning, not as an authoritative source 
              for competitive Magic.
            </p>
          </section>

          {/* The Road Ahead */}
          <section style={{ marginBottom: spacing['2xl'] }}>
            <h2 style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
              })
            }}>
              The Road Ahead
            </h2>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Judgy is just getting started. We're building features that MTG players actually 
              want, and we're listening to your feedback.
            </p>

            {/* Coming Soon */}
            <div style={{ marginBottom: spacing.xl }}>
              <h3 style={{
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.semibold,
                marginBottom: spacing.md,
                color: theme.brand.primary,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.lg,
                })
              }}>
                Launching Soon
              </h3>
              <p style={{
                fontSize: typography.fontSize.sm,
                color: theme.text.muted,
                marginBottom: spacing.md,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.xs,
                })
              }}>
                These features are actively in development:
              </p>
              <ul style={{
                margin: 0,
                paddingLeft: spacing.xl,
                color: theme.text.secondary,
                fontSize: typography.fontSize.base,
                lineHeight: typography.lineHeight.relaxed,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.sm,
                  paddingLeft: mobileSpacing.lg,
                })
              }}>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Conversation History (Premium only)</strong> - Never lose a ruling again. Premium 
                  users can save, search, and revisit all their past questions.
                </li>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Search & Favorites (Premium only)</strong> - Bookmark your most-used rulings for 
                  quick reference.
                </li>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Priority Responses (Premium only)</strong> - Get answers even faster during those 
                  critical mid-game moments.
                </li>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Confidence Scores</strong> - See how certain Judgy is about each 
                  ruling, so you know when to double-check with a judge.
                </li>
              </ul>
            </div>

            {/* On the Horizon */}
            <div style={{ marginBottom: spacing.xl }}>
              <h3 style={{
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.semibold,
                marginBottom: spacing.md,
                color: theme.brand.primary,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.lg,
                })
              }}>
                On the Horizon
              </h3>
              <p style={{
                fontSize: typography.fontSize.sm,
                color: theme.text.muted,
                marginBottom: spacing.md,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.xs,
                })
              }}>
                Features we're exploring based on community feedback:
              </p>
              <ul style={{
                margin: 0,
                paddingLeft: spacing.xl,
                color: theme.text.secondary,
                fontSize: typography.fontSize.base,
                lineHeight: typography.lineHeight.relaxed,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.sm,
                  paddingLeft: mobileSpacing.lg,
                })
              }}>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Deep Rulings Mode</strong> - Step-by-step breakdowns of complex 
                  interactions for competitive players.
                </li>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Discord Bot</strong> - Ask Judgy directly from your Discord server.
                </li>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Multi-Language Support</strong> - Get support in your language.
                </li>
                <li style={{ marginBottom: spacing.sm }}>
                  <strong>Expand to other TCGs</strong> - Provide support to other complex trading card games.
                </li>
              </ul>
            </div>

            {/* Your Ideas */}
            <div style={{
              backgroundColor: theme.brand.primary,
              padding: spacing.xl,
              borderRadius: '8px',
              textAlign: 'center',
              ...(isMobile() && {
                padding: mobileSpacing.lg,
              })
            }}>
              <h3 style={{
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.semibold,
                color: theme.text.inverse,
                marginBottom: spacing.md,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.lg,
                })
              }}>
                What Do You Want?
              </h3>
              <p style={{
                fontSize: typography.fontSize.base,
                color: theme.text.inverse,
                marginBottom: spacing.lg,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.sm,
                })
              }}>
                Have a feature request? We're all ears!
              </p>
              <a 
                href="mailto:support@askjudgy.com"
                style={mergeStyles(
                  buttonStyles.base,
                  {
                    backgroundColor: theme.text.inverse,
                    color: theme.brand.primary,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(isMobile() && {
                      ...buttonStyles.baseMobile,
                    })
                  }
                )}
              >
                Email Us Your Ideas
              </a>
            </div>

            <p style={{
              fontSize: typography.fontSize.xs,
              color: theme.text.muted,
              marginTop: spacing.lg,
              fontStyle: 'italic',
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xs,
              })
            }}>
              Last updated: November 2025
            </p>
          </section>

          {/* Free Forever */}
          <section style={{ marginBottom: spacing['2xl'] }}>
            <h2 style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
              })
            }}>
              Free Forever, Premium Available
            </h2>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Judgy's core features are free to use, forever. No ads, no paywalls for basic 
              functionality—just instant MTG rules help whenever you need it.
            </p>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Premium membership ($5/month) adds power-user features like conversation history, 
              faster responses, and advanced tools—plus it helps support ongoing development and 
              keeps the lights on. But you'll never need Premium to get a great answer to your 
              rules question.
            </p>
          </section>

          {/* Who Made This */}
          <section>
            <h2 style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xl,
              })
            }}>
              Who Made This?
            </h2>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              marginBottom: spacing.md,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Judgy was created by an MTG player who got tired of endlessly scrolling through 
              forum threads and judge blogs to understand complicated interactions. Built with 
              modern AI technology and a deep respect for the game's complexity, Judgy aims to 
              make Magic more accessible to everyone.
            </p>
            <p style={{
              fontSize: typography.fontSize.base,
              lineHeight: typography.lineHeight.relaxed,
              color: theme.text.secondary,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.base,
              })
            }}>
              Questions? Feedback? Just want to say hi? Reach out at{' '}
              <a 
                href="mailto:support@askjudgy.com"
                style={{
                  color: theme.brand.primary,
                  textDecoration: 'none',
                  fontWeight: typography.fontWeight.medium,
                }}
              >
                support@askjudgy.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </>
  );
}